import { Palette } from '@mui/material';

import { VectorImageName } from '~components/media/vectorImage';

enum StatusVariant {
	Neutral,
	Brand,
	Success,
	Warning,
	Destructive,
	Info,
}

export const getStatusForegroundColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.fgQuaternary.main;
		case StatusVariant.Brand:
			return palette.fgBrandSecondary.main;
		case StatusVariant.Success:
			return palette.fgSuccessSecondary.main;
		case StatusVariant.Warning:
			return palette.fgWarningSecondary.main;
		case StatusVariant.Destructive:
			return palette.fgErrorSecondary.main;
		case StatusVariant.Info:
			return palette.fgInfoSecondary.main;
	}
};

export const getStatusIconColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.common.black;
		case StatusVariant.Brand:
			return palette.fgBrandSecondary.main;
		case StatusVariant.Success:
			return palette.fgSuccessSecondary.main;
		case StatusVariant.Warning:
			return palette.fgWarningSecondary.main;
		case StatusVariant.Destructive:
			return palette.fgErrorSecondary.main;
		case StatusVariant.Info:
			return palette.fgInfoSecondary.main;
	}
};

export const getStatusBackgroundColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.bgTertiary.main;
		case StatusVariant.Brand:
			return palette.bgBrandSecondary.main;
		case StatusVariant.Success:
			return palette.bgSuccessSecondary.main;
		case StatusVariant.Warning:
			return palette.bgWarningSecondary.main;
		case StatusVariant.Destructive:
			return palette.bgErrorSecondary.main;
		case StatusVariant.Info:
			return palette.bgInfoSecondary.main;
	}
};

export const getButtonBackgroundColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.bgTertiary.main;
		case StatusVariant.Brand:
			return palette.bgBrandSolid.main;
		case StatusVariant.Success:
			return palette.bgSuccessSolid.main;
		case StatusVariant.Warning:
			return palette.bgWarningSolid.main;
		case StatusVariant.Destructive:
			return palette.bgErrorSolid.main;
		case StatusVariant.Info:
			return palette.bgInfoSolid.main;
	}
};

export const getTextButtonHoverColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.bgTertiary.main;
		case StatusVariant.Brand:
			return palette.bgBrandPrimary.main;
		case StatusVariant.Success:
			return palette.bgSuccessPrimary.main;
		case StatusVariant.Warning:
			return palette.bgWarningPrimary.main;
		case StatusVariant.Destructive:
			return palette.bgErrorPrimary.main;
		case StatusVariant.Info:
			return palette.bgInfoPrimary.main;
	}
};

export const getButtonHoverColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.bgQuaternary.main;
		case StatusVariant.Brand:
			return palette.bgBrandSolidHover.main;
		case StatusVariant.Success:
			return palette.bgSuccessSolid.main;
		case StatusVariant.Warning:
			return palette.bgWarningSolid.main;
		case StatusVariant.Destructive:
			return palette.bgErrorSolidHover.main;
		case StatusVariant.Info:
			return palette.bgInfoSolid.main;
	}
};

export const getButtonLabelColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
		case StatusVariant.Brand:
			return palette.textPrimary.main;
		case StatusVariant.Success:
		case StatusVariant.Warning:
		case StatusVariant.Destructive:
		case StatusVariant.Info:
			return palette.common.white;
	}
};

export const getBorderedLabelColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.textSecondary.main;
		case StatusVariant.Brand:
			return palette.textBrandPrimary.main;
		case StatusVariant.Success:
			return palette.textSuccess.main;
		case StatusVariant.Warning:
			return palette.textWarning.main;
		case StatusVariant.Destructive:
			return palette.textError.main;
		case StatusVariant.Info:
			return palette.textInfo.main;
	}
};

export const getBorderedBorderColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.borderPrimary.main;
		case StatusVariant.Brand:
			return palette.textBrandPrimary.main;
		case StatusVariant.Success:
			return palette.textSuccess.main;
		case StatusVariant.Warning:
			return palette.textWarning.main;
		case StatusVariant.Destructive:
			return palette.textError.main;
		case StatusVariant.Info:
			return palette.textInfo.main;
	}
};

export const getButtonBorderColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.borderSecondary.main;
		case StatusVariant.Brand:
			return palette.borderBrandPrimary.main;
		case StatusVariant.Success:
			return palette.borderSuccess.main;
		case StatusVariant.Warning:
			return palette.borderWarning.main;
		case StatusVariant.Destructive:
			return palette.borderError.main;
		case StatusVariant.Info:
			return palette.borderInfo.main;
	}
};

export const getButtonBorderHoverColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.borderPrimary.main;
		case StatusVariant.Brand:
			return palette.borderBrandPrimary.main;
		case StatusVariant.Success:
			return palette.borderSuccess.main;
		case StatusVariant.Warning:
			return palette.borderWarning.main;
		case StatusVariant.Destructive:
			return palette.bgErrorSolidHover.main;
		case StatusVariant.Info:
			return palette.borderInfo.main;
	}
};

export const getButtonIconColor = (variant: StatusVariant, palette: Palette): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.common.black;
		case StatusVariant.Brand:
			return palette.fgBrandPrimary.main;
		case StatusVariant.Success:
			return palette.fgSuccessPrimary.main;
		case StatusVariant.Warning:
			return palette.fgWarningPrimary.main;
		case StatusVariant.Destructive:
			return palette.fgErrorPrimary.main;
		case StatusVariant.Info:
			return palette.fgInfoPrimary.main;
	}
};

export const getNotificationCardIcon = (variant: StatusVariant): VectorImageName => {
	switch (variant) {
		case StatusVariant.Warning:
			return 'status/warning';
		case StatusVariant.Success:
			return 'status/success';
		case StatusVariant.Destructive:
			return 'status/destructive';
		default:
			// TODO: Add correct icons for other variants
			return 'icon/swap';
	}
};

export const getNotificationBackgroundColor = (
	variant: StatusVariant,
	palette: Palette
): string => {
	switch (variant) {
		case StatusVariant.Neutral:
			return palette.bgSecondary.main;
		case StatusVariant.Brand:
			return palette.bgBrandPrimary.main;
		case StatusVariant.Success:
			return palette.bgSuccessPrimary.main;
		case StatusVariant.Warning:
			return palette.bgWarningPrimary.main;
		case StatusVariant.Destructive:
			return palette.bgErrorPrimary.main;
		case StatusVariant.Info:
			return palette.bgInfoPrimary.main;
	}
};

export default StatusVariant;
